import useSWR, { mutate } from "swr"
import API from "./api"

export default function useWorkflow(id) {
  const path = `/workflows/${id}`

  const key = !id ? undefined : `/workflows/${id}`

  const { data: workflow, error } = useSWR(key, API.get, { suspense: true })

  async function update(body) {
    await API.put(path, body)
    mutate(key)
  }

  async function destroy() {
    await API.delete(path)
  }

  async function refresh() {
    mutate(key)
  }

  function updateStage(stageId) {
    return async (data) => {
      await API.put(`${path}/stages/${stageId}`, data)
      mutate(key)
    }
  }

  async function addTask(data) {
    const newTask = await API.post(`${path}/tasks`, data)
    mutate(key)

    return {
      ...newTask,
      update: updateTask(newTask.id),
      destroy: destroyTask(newTask.id),
    }
  }

  function addStageTask(stageId) {
    return async (data) => {
      const newTask = await API.post(`${path}/stages/${stageId}/tasks`, data)
      mutate(key)

      return {
        ...newTask,
        update: updateTask(newTask.id),
        destroy: destroyTask(newTask.id),
      }
    }
  }

  function updateTask(taskId) {
    return async (data) => {
      const taskKey = `/tasks/${taskId}`
      await mutate(taskKey, API.put(taskKey, data))

      mutate(key)
    }
  }

  function destroyTask(taskId) {
    return async () => {
      await API.delete(`tasks/${taskId}`)
      mutate(key)
    }
  }

  function reorderStageCategory(stage) {
    return async ({ categoryId, to }) => {
      await API.put(
        `${path}/stages/${stage.id}/categories/${categoryId}/order`,
        {
          section: "task",
          to,
        }
      )
      mutate(key)
    }
  }

  async function reorderTasks({ taskId, from, to }) {
    await API.put(`${path}/tasks/${taskId}/order`, { from, to })
    mutate(key)
  }

  function updateLocalTasks(reorderedTasksByCategory) {
    const newData = {
      ...workflow,
      tasks_by_category: reorderedTasksByCategory,
    }
    mutate(key, newData, false)
  }

  function updateLocalStageTasks(stageId) {
    return async (reorderedTasks) => {
      const stageIndex = workflow.stages.findIndex(
        (stage) => stage.id === stageId
      )
      workflow.stages[stageIndex].tasks_by_category = reorderedTasks
      mutate(key, { ...workflow }, false)
    }
  }

  if (error) return undefined
  if (!workflow) return undefined

  workflow.update = update
  workflow.destroy = destroy
  workflow.refresh = refresh
  workflow.tasks_by_category.add = addTask
  workflow.tasks_by_category.reorderTasks = reorderTasks
  workflow.tasks_by_category.updateLocal = updateLocalTasks

  for (const category of workflow.tasks_by_category) {
    for (const task of category.tasks) {
      task.refresh = refresh
      task.update = updateTask(task.id)
      task.destroy = destroyTask(task.id)
    }
  }

  for (const stage of workflow.stages) {
    stage.update = updateStage(stage.id)
    stage.refresh = refresh

    stage.tasks_by_category.add = addStageTask(stage.id)
    stage.tasks_by_category.updateLocal = updateLocalStageTasks(stage.id)
    stage.tasks_by_category.reorderCategory = reorderStageCategory(stage)
    stage.tasks_by_category.reorderTasks = reorderTasks

    for (const category of stage.tasks_by_category) {
      for (const task of category.tasks) {
        task.refresh = refresh
        task.update = updateTask(task.id)
        task.destroy = destroyTask(task.id)
      }
    }
  }

  return workflow
}
